import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError, firstValueFrom, map, Observable, throwError } from 'rxjs';
import * as _ from 'lodash-es';
import moment from 'moment';

@Injectable({
  providedIn: 'root'
})
export class EntityFillingService {

  constructor(private http: HttpClient) { }

  private getHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });
  }

  private handleError(error: any): Observable<never> {
    console.error('An error occurred:', error);
    return throwError(error.message || 'Server error');
  }

  private formatParams(params: any): string {
    return Object.keys(params).map(key => {
      let value = params[key];
      if (value instanceof Date) {
        value = moment(value).format('MM/DD/YYYY');
      }
      return `${key}=${encodeURIComponent(value)}`;
    }).join('&');
  }

  // 🚀 CRITICAL: Create method with proper parameters
  saveEntityFilingForm(
    baseUrl: string,
    formData: any,
    globalService: any,
    filterParams: any,
    rowData: any
  ): Observable<any> {
    
    // 🚀 CRITICAL: Use correct URL construction for create
    const url = `${baseUrl}/saveJsonObject?action_code=qd33ho`;

    // Build params directly from the formData payload structure
    let params: any = {};
    
    // Core JSON payload fields
    params.jsonSettings = formData.jsonSettings;
    params.json_data = formData.json_data;
    params.tax_year = formData.tax_year ?? filterParams?.tax_year ?? rowData?.TAX_YEAR ?? globalService?.tax_year ?? globalService?.globalParams?.tax_year;
    params.screen_key = formData.screen_key || filterParams?.screen_key || globalService?.screen_key || '11167';
    params.scenario = formData.scenario ?? filterParams?.scenario ?? rowData?.SCENARIO ?? globalService?.scenario ?? globalService?.globalParams?.scenario;
    params.jcd_key = formData.jcd_key ?? filterParams?.jcd_key ?? rowData?.JCD_KEY ?? globalService?.jcd_key ?? globalService?.globalParams?.jcd_key;
    params.jcd_ta_key = formData.jcd_ta_key || filterParams?.jcd_ta_key || globalService?.jcd_ta_key || '5133';
    params.activity_key = formData.activity_key || filterParams?.activity_key || globalService?.activity_key || '5124';
    params.screen_type = formData.screen_type || 'grid';
    params.is_locked = formData.is_locked || 'N';
    params.issue_id = formData.issue_id || filterParams?.issue_id || globalService?.issue_id || '';
    params.issue_short_desc = formData.issue_short_desc || filterParams?.issue_short_desc || globalService?.issue_short_desc || '';
    params.issue_id_for_tx_save = formData.issue_id_for_tx_save || filterParams?.issue_id_for_tx_save || globalService?.issue_id_for_tx_save || '';
    params.is_issue_key_enabled = formData.is_issue_key_enabled || 'N';
    params.scenarioCode = formData.scenarioCode || filterParams?.scenarioCode || globalService?.scenarioCode || 'RAF';
    // 🚀 CRITICAL: Use application/x-www-form-urlencoded (NOT json)
    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      urlEncoded.set(key, params[key]);
    });

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded'
      })
    };

    return this.http.post<any>(url, urlEncoded.toString(), { ...httpOptions, withCredentials: true });
  }

  // 🚀 CRITICAL: Update method with proper parameters
  updateEntityFilingForm(
    baseUrl: string,
    formData: any,
    globalService: any,
    filterParams: any,
    rowData: any
  ): Observable<any> {
    
    // 🚀 CRITICAL: Use correct URL construction for update
    const url = `${baseUrl}/saveJsonObject?action_code=lrpf1c`;

    // Build params directly from the formData payload structure
    let params: any = {};
    
    // Core JSON payload fields
    params.jsonSettings = formData.jsonSettings;
    params.json_data = formData.json_data;
    params.tax_year = formData.tax_year ?? filterParams?.tax_year ?? rowData?.TAX_YEAR ?? globalService?.tax_year ?? globalService?.globalParams?.tax_year;
    params.screen_key = formData.screen_key || filterParams?.screen_key || globalService?.screen_key || '11167';
    params.scenario = formData.scenario ?? filterParams?.scenario ?? rowData?.SCENARIO ?? globalService?.scenario ?? globalService?.globalParams?.scenario;
    params.jcd_key = formData.jcd_key ?? filterParams?.jcd_key ?? rowData?.JCD_KEY ?? globalService?.jcd_key ?? globalService?.globalParams?.jcd_key;
    params.jcd_ta_key = formData.jcd_ta_key || filterParams?.jcd_ta_key || globalService?.jcd_ta_key || '5133';
    params.activity_key = formData.activity_key || filterParams?.activity_key || globalService?.activity_key || '5124';
    params.screen_type = formData.screen_type || 'grid';
    params.is_locked = formData.is_locked || 'N';
    params.issue_id = formData.issue_id || filterParams?.issue_id || globalService?.issue_id || '';
    params.issue_short_desc = formData.issue_short_desc || filterParams?.issue_short_desc || globalService?.issue_short_desc || '';
    params.issue_id_for_tx_save = formData.issue_id_for_tx_save || filterParams?.issue_id_for_tx_save || globalService?.issue_id_for_tx_save || '';
    params.is_issue_key_enabled = formData.is_issue_key_enabled || 'N';
    params.scenarioCode = formData.scenarioCode || filterParams?.scenarioCode || globalService?.scenarioCode || 'RAF';
    // 🚀 CRITICAL: Use application/x-www-form-urlencoded (NOT json)
    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      urlEncoded.set(key, params[key]);
    });

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded'
      })
    };

    return this.http.post<any>(url, urlEncoded.toString(), { ...httpOptions, withCredentials: true });
  }

  // 🚀 CRITICAL: Remove method with proper parameters
  removeEntityFilingForm(
    baseUrl: string,
    taxYear: string,
    groupObjKey: string,
    legalEntityId: string,
    globalService: any,
    filterParams: any,
    rowData: any
  ): Observable<any> {
    
    // 🚀 CRITICAL: Use correct URL construction for remove
    const url = `${baseUrl}/saveJsonObject?action_code=e2evpp`;

    // Build params to match exact payload structure
    let params: any = {};
    
    params.tax_year = taxYear ?? filterParams?.tax_year ?? rowData?.TAX_YEAR ?? globalService?.tax_year ?? globalService?.globalParams?.tax_year;
    params.group_obj_key = groupObjKey || '';
    params.legal_entity_id = legalEntityId || '';
    params.screen_key = filterParams?.screen_key || globalService?.screen_key || '11167';
    params.scenario = filterParams?.scenario ?? rowData?.SCENARIO ?? globalService?.scenario ?? globalService?.globalParams?.scenario;
    params.jcd_key = filterParams?.jcd_key ?? rowData?.JCD_KEY ?? globalService?.jcd_key ?? globalService?.globalParams?.jcd_key;
    params.jcd_ta_key = filterParams?.jcd_ta_key || globalService?.jcd_ta_key || '5133';
    params.activity_key = filterParams?.activity_key || globalService?.activity_key || '5124';
    params.screen_type = 'grid';
    params.is_locked = 'N';
    params.issue_id = filterParams?.issue_id || globalService?.issue_id || '';
    params.issue_short_desc = filterParams?.issue_short_desc || globalService?.issue_short_desc || '';
    params.issue_id_for_tx_save = filterParams?.issue_id_for_tx_save || globalService?.issue_id_for_tx_save || '';
    params.scenarioCode = filterParams?.scenarioCode || globalService?.scenarioCode || 'RAF';
    params.is_issue_key_enabled = filterParams?.is_issue_key_enabled || globalService?.is_issue_key_enabled || 'N';
    // 🚀 CRITICAL: Use application/x-www-form-urlencoded (NOT json)
    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      urlEncoded.set(key, params[key]);
    });

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded'
      })
    };

    return this.http.post<any>(url, urlEncoded.toString(), { ...httpOptions, withCredentials: true });
  }

  // 🚀 CRITICAL: Delete method with proper parameters
  deleteEntityFilingForm(
    baseUrl: string,
    taxYear: string,
    groupObjKey: string,
    legalEntityId: string,
    globalService: any,
    filterParams: any,
    rowData: any
  ): Observable<any> {
    
    // 🚀 CRITICAL: Use correct URL construction for delete
    const url = `${baseUrl}/saveJsonObject?action_code=h6eb8q`;

    // Build params to match exact payload structure
    let params: any = {};
    
    params.tax_year = taxYear ?? filterParams?.tax_year ?? rowData?.TAX_YEAR ?? globalService?.tax_year ?? globalService?.globalParams?.tax_year;
    params.group_obj_key = groupObjKey || '';
    params.legal_entity_id = legalEntityId || '';
    params.screen_key = filterParams?.screen_key || globalService?.screen_key || '11167';
    params.scenario = filterParams?.scenario ?? rowData?.SCENARIO ?? globalService?.scenario ?? globalService?.globalParams?.scenario;
    params.jcd_key = filterParams?.jcd_key ?? rowData?.JCD_KEY ?? globalService?.jcd_key ?? globalService?.globalParams?.jcd_key;
    params.jcd_ta_key = filterParams?.jcd_ta_key || globalService?.jcd_ta_key || '5133';
    params.activity_key = filterParams?.activity_key || globalService?.activity_key || '5124';
    params.screen_type = 'grid';
    params.is_locked = 'N';
    params.issue_id = filterParams?.issue_id || globalService?.issue_id || '';
    params.issue_short_desc = filterParams?.issue_short_desc || globalService?.issue_short_desc || '';
    params.issue_id_for_tx_save = filterParams?.issue_id_for_tx_save || globalService?.issue_id_for_tx_save || '';
    params.scenarioCode = filterParams?.scenarioCode || globalService?.scenarioCode || 'RAF';
    params.is_issue_key_enabled = filterParams?.is_issue_key_enabled || globalService?.is_issue_key_enabled || 'N';
    // 🚀 CRITICAL: Use application/x-www-form-urlencoded (NOT json)
    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      urlEncoded.set(key, params[key]);
    });

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded'
      })
    };

    return this.http.post<any>(url, urlEncoded.toString(), { ...httpOptions, withCredentials: true });
  }

  // Data loading methods
  loadScenarioTypeCode(baseUrl: string, filterParams: any, rowData: any): Promise<any[]> {
    const baseParams: any = {
      action_id: 32735,
      action_code: '73pnl0',
      activity_key: filterParams?.activity_key || rowData?.ACTIVITY_KEY || 5124,
      is_locked: filterParams?.is_locked || 'N',
      issue_id: filterParams?.issue_id || '',
      issue_id_for_tx_save: filterParams?.issue_id_for_tx_save || '',
      issue_short_desc: filterParams?.issue_short_desc || '',
      jcd_key: filterParams?.jcd_key || rowData?.JCD_KEY || 250,
      jcd_ta_key: filterParams?.jcd_ta_key || rowData?.JCD_TA_KEY || 5133,
      scenario: filterParams?.scenario || rowData?.SCENARIO || 74,
      screen_key: filterParams?.screen_key || rowData?.SCREEN_KEY || 11167,
      screen_type: filterParams?.screen_type || 'grid',
      tax_year: filterParams?.tax_year ?? rowData?.TAX_YEAR,
      scenario_type_code: null,
      scenario_desc: null
    };

    // 🚀 CRITICAL: Build URL with query parameters for GET request
    const queryParams = new URLSearchParams();
    Object.keys(baseParams).forEach(key => {
      if (baseParams[key] !== null && baseParams[key] !== undefined) {
        queryParams.set(key, baseParams[key]);
      }
    });

    const url = `${baseUrl}/loadJsonObject?${queryParams.toString()}`;
    
    return firstValueFrom(this.http.get(url).pipe(
      map((response: any) => {
        if (response.callSuccess === "1") {
          return response.jsonObject;
        } else {
          this.showAlert('error', response.errorMessage || 'Unable to retrieve Scenario Type Code.', 4000);
          return [];
        }
      }),
      catchError(this.handleError)
    ));
  }

  loadScenarioDesc(baseUrl: string, filterParams: any, rowData: any, selectedValue: string): Promise<any[]> {
    const baseParams: any = {
      action_id: 32735,
      action_code: '73pnl0',
      activity_key: filterParams?.activity_key || rowData?.ACTIVITY_KEY || 5124,
      is_locked: filterParams?.is_locked || 'N',
      issue_id: filterParams?.issue_id || '',
      issue_id_for_tx_save: filterParams?.issue_id_for_tx_save || '',
      issue_short_desc: filterParams?.issue_short_desc || '',
      jcd_key: filterParams?.jcd_key || rowData?.JCD_KEY || 250,
      jcd_ta_key: filterParams?.jcd_ta_key || rowData?.JCD_TA_KEY || 5133,
      scenario: filterParams?.scenario || rowData?.SCENARIO || 74,
      screen_key: filterParams?.screen_key || rowData?.SCREEN_KEY || 11167,
      screen_type: filterParams?.screen_type || 'grid',
      tax_year: filterParams?.tax_year ?? rowData?.TAX_YEAR,
      scenario_type_code: selectedValue,
      scenario_desc: null
    };

    // 🚀 CRITICAL: Build URL with query parameters for GET request
    const queryParams = new URLSearchParams();
    Object.keys(baseParams).forEach(key => {
      if (baseParams[key] !== null && baseParams[key] !== undefined) {
        queryParams.set(key, baseParams[key]);
      }
    });

    const url = `${baseUrl}/loadJsonObject?${queryParams.toString()}`;
    
    return firstValueFrom(this.http.get(url).pipe(
      map((response: any) => {
        if (response.callSuccess === "1") {
          return response.jsonObject;
        } else {
          this.showAlert('error', response.errorMessage || 'Unable to retrieve Scenario Description.', 4000);
          return [];
        }
      }),
      catchError(this.handleError)
    ));
  }

  loadGroupObjKey(baseUrl: string, filterParams: any, rowData: any, scenarioTypeCode: string, scenarioDesc: string): Promise<any[]> {
    const baseParams: any = {
      action_id: 32735,
      action_code: '73pnl0',
      activity_key: filterParams?.activity_key || rowData?.ACTIVITY_KEY || 5124,
      is_locked: filterParams?.is_locked || 'N',
      issue_id: filterParams?.issue_id || '',
      issue_id_for_tx_save: filterParams?.issue_id_for_tx_save || '',
      issue_short_desc: filterParams?.issue_short_desc || '',
      jcd_key: filterParams?.jcd_key || rowData?.JCD_KEY || 250,
      jcd_ta_key: filterParams?.jcd_ta_key || rowData?.JCD_TA_KEY || 5133,
      scenario: filterParams?.scenario || rowData?.SCENARIO || 74,
      screen_key: filterParams?.screen_key || rowData?.SCREEN_KEY || 11167,
      screen_type: filterParams?.screen_type || 'grid',
      tax_year: filterParams?.tax_year ?? rowData?.TAX_YEAR,
      scenario_type_code: scenarioTypeCode,
      scenario_desc: scenarioDesc
    };

    // 🚀 CRITICAL: Build URL with query parameters for GET request
    const queryParams = new URLSearchParams();
    Object.keys(baseParams).forEach(key => {
      if (baseParams[key] !== null && baseParams[key] !== undefined) {
        queryParams.set(key, baseParams[key]);
      }
    });

    const url = `${baseUrl}/loadJsonObject?${queryParams.toString()}`;
    
    return firstValueFrom(this.http.get(url).pipe(
      map((response: any) => {
        if (response.callSuccess === "1") {
          return response.jsonObject;
        } else {
          this.showAlert('error', response.errorMessage || 'Unable to retrieve Group Obj Key.', 4000);
          return [];
        }
      }),
      catchError(this.handleError)
    ));
  }

  loadAllEntities(baseUrl: string, groupObjKey: string): Promise<any[]> {
    const baseParams: any = {
      action_id: 32823,
      action_code: 'lvgt0l',
      group_obj_key: groupObjKey
    };

    // 🚀 CRITICAL: Build URL with query parameters for GET request
    const queryParams = new URLSearchParams();
    Object.keys(baseParams).forEach(key => {
      if (baseParams[key] !== null && baseParams[key] !== undefined) {
        queryParams.set(key, baseParams[key]);
      }
    });

    const url = `${baseUrl}/loadJsonObject?${queryParams.toString()}`;
    
    return firstValueFrom(this.http.get(url).pipe(
      map((response: any) => {
        if (response.callSuccess === "1") {
          return response.jsonObject;
        } else {
          this.showAlert('error', response.errorMessage || 'Unable to retrieve entities.', 4000);
          return [];
        }
      }),
      catchError(this.handleError)
    ));
  }

  loadAllFilingCycleType(baseUrl: string, filterParams: any, rowData: any): Promise<any[]> {
    const baseParams: any = {
      action_id: 32824,
      action_code: 'gexp8r',
      activity_key: filterParams?.activity_key || rowData?.ACTIVITY_KEY || 5124,
      is_locked: filterParams?.is_locked || 'N',
      issue_id: filterParams?.issue_id || '',
      issue_id_for_tx_save: filterParams?.issue_id_for_tx_save || '',
      issue_short_desc: filterParams?.issue_short_desc || '',
      jcd_key: filterParams?.jcd_key || rowData?.JCD_KEY || 250,
      jcd_ta_key: filterParams?.jcd_ta_key || rowData?.JCD_TA_KEY || 5133,
      scenario: filterParams?.scenario || rowData?.SCENARIO || 74,
      screen_key: filterParams?.screen_key || rowData?.SCREEN_KEY || 11167,
      screen_type: filterParams?.screen_type || 'grid',
      tax_year: filterParams?.tax_year ?? rowData?.TAX_YEAR
    };

    // 🚀 CRITICAL: Build URL with query parameters for GET request
    const queryParams = new URLSearchParams();
    Object.keys(baseParams).forEach(key => {
      if (baseParams[key] !== null && baseParams[key] !== undefined) {
        queryParams.set(key, baseParams[key]);
      }
    });

    const url = `${baseUrl}/loadJsonObject?${queryParams.toString()}`;
    
    return firstValueFrom(this.http.get(url).pipe(
      map((response: any) => {
        if (response.callSuccess === "1") {
          return response.jsonObject;
        } else {
          this.showAlert('error', response.errorMessage || 'Unable to retrieve Filing Cycle Types.', 4000);
          return [];
        }
      }),
      catchError(this.handleError)
    ));
  }

  loadAllAppScenario(baseUrl: string, filterParams: any, rowData: any): Promise<any[]> {
    const baseParams: any = {
      action_id: 32825,
      action_code: 'sra6sw',
      activity_key: filterParams?.activity_key || rowData?.ACTIVITY_KEY || 5124,
      is_locked: filterParams?.is_locked || 'N',
      issue_id: filterParams?.issue_id || '',
      issue_id_for_tx_save: filterParams?.issue_id_for_tx_save || '',
      issue_short_desc: filterParams?.issue_short_desc || '',
      jcd_key: filterParams?.jcd_key || rowData?.JCD_KEY || 250,
      jcd_ta_key: filterParams?.jcd_ta_key || rowData?.JCD_TA_KEY || 5133,
      scenario: filterParams?.scenario || rowData?.SCENARIO || 74,
      screen_key: filterParams?.screen_key || rowData?.SCREEN_KEY || 11167,
      screen_type: filterParams?.screen_type || 'grid',
      tax_year: filterParams?.tax_year ?? rowData?.TAX_YEAR
    };

    // 🚀 CRITICAL: Build URL with query parameters for GET request
    const queryParams = new URLSearchParams();
    Object.keys(baseParams).forEach(key => {
      if (baseParams[key] !== null && baseParams[key] !== undefined) {
        queryParams.set(key, baseParams[key]);
      }
    });

    const url = `${baseUrl}/loadJsonObject?${queryParams.toString()}`;
    
    return firstValueFrom(this.http.get(url).pipe(
      map((response: any) => {
        if (response.callSuccess === "1") {
          return response.jsonObject;
        } else {
          this.showAlert('error', response.errorMessage || 'Unable to retrieve App Scenarios.', 4000);
          return [];
        }
      }),
      catchError(this.handleError)
    ));
  }

  convertDateFormats(jsonObj: any): any {
    if (jsonObj.TAX_YEAR_END) {
      jsonObj.TAX_YEAR_END = moment(jsonObj.TAX_YEAR_END).format('MM/DD/YYYY');
    }
    if (jsonObj.TAX_YEAR_BEGIN) {
      jsonObj.TAX_YEAR_BEGIN = moment(jsonObj.TAX_YEAR_BEGIN).format('MM/DD/YYYY');
    }
    return jsonObj;
  }

  showAlert(type: 'success' | 'error' | 'warn', message: string, duration: number): void {
    // Placeholder for AlertService. In a real Angular app, you'd inject a proper Angular Material Snackbar or similar.
    console.log(`Alert (${type}): ${message}`);
  }
}
